import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './CompleteProfilePage.css'; // Assuming a new CSS file for styling
import { upsertProfile } from '../services/profileService';
import { supabase } from '../services/supabaseClient';

function CompleteProfilePage({ user, profile, profileComplete, onProfileUpdate }) {
  const [fullName, setFullName] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [branch, setBranch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // If profile data is already available and complete, redirect
    if (profileComplete === true) {
      navigate('/dashboard');
      return; // Ensure to return after navigation to prevent further execution
    }

    // Pre-fill form if profile data exists
    if (profile) {
      setFullName(profile.full_name || '');
      setAcademicYear(profile.academic_year || '');
      setBranch(profile.branch || '');
    }
  }, [user, navigate, profile, profileComplete]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!fullName || !academicYear || !branch) {
      setError('All fields are required.');
      setLoading(false);
      return;
    }

    try {
      const updates = {
        id: user.id,
        full_name: fullName,
        academic_year: academicYear,
        branch,
      };

      const { error: updateError } = await upsertProfile(updates);

      if (updateError) {
        console.error("Profile update failed:", updateError);
        throw updateError;
      }

      setSuccess('Profile updated successfully!');

      // Notify parent component to update global state immediately
      if (onProfileUpdate) {
        await onProfileUpdate();
      }

      // Re-fetch the newly created profile using direct Supabase call just to be sure
      // (though onProfileUpdate should have handled it)
      const { data: refreshedProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (fetchError) {
        // If onProfileUpdate succeeded, we might still be okay, but let's log
        console.warn("Direct verification after update had an issue, but proceeding if parent update worked:", fetchError.message);
      }

      // If refreshedProfile exists, immediately navigate to dashboard
      // Note: We bypass the strict isProfileDataComplete check here because we just successfully upserted valid data.
      if (refreshedProfile) {
        navigate("/dashboard", { replace: true });
        return;
      }

    } catch (err) {
      console.error('Error updating profile:', err.message);
      setError('Failed to update profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [fullName, academicYear, branch, user, navigate, onProfileUpdate]);

  if (!user || profileComplete === true) {
    // Should be redirected by useEffect, but handle defensively
    return null;
  }

  return (
    <div className="complete-profile-page-container">
      <div className="complete-profile-form-card">
        <h2>Complete Your Profile</h2>
        <p className="profile-subtitle">Please provide the following details to access the dashboard.</p>

        <form onSubmit={handleSubmit}>
          {error && <p className="error-message">{error}</p>}
          {success && <p className="success-message">{success}</p>}

          <div className="form-group">
            <label htmlFor="fullName">Full Name</label>
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="academicYear">Academic Year</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">Select Year</option>
              <option value="1st">1st Year</option>
              <option value="2nd">2nd Year</option>
              <option value="3rd">3rd Year</option>
              <option value="4th">4th Year</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="branch">Branch/Major</label>
            <input
              type="text"
              id="branch"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="save-profile-button" disabled={loading}>
            {loading ? 'Saving...' : 'Complete Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CompleteProfilePage;
